import Logout from "@mui/icons-material/Logout";
import Person from "@mui/icons-material/Person";
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";

import type { MouseEvent } from "react";
import { useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@/app/store";
import { selectCurrentUser } from "@/features/users/model/usersSlice";
import { ThemeSelector } from "@/shared/ui/layout/ThemeSelector";

function emailInitial(email: string): string {
  const local = email.split("@")[0]?.trim() ?? "";
  const ch = local[0] ?? "?";
  return ch.toUpperCase();
}

function UserAvatar({ email }: { email: string }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: "1rem",
        fontWeight: 600,
        bgcolor: "var(--theme-primary, #395C3C)",
        color: "var(--theme-text-on-primary, #F5F7FA)",
      }}
    >
      {emailInitial(email)}
    </Box>
  );
}

type UserAccountMenuProps = {
  onLogout: () => void;
};

export function UserAccountMenu({ onLogout }: UserAccountMenuProps) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const email = currentUser?.email ?? "";
  const menuId = useId();
  const buttonId = useId();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const openMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleLogout = useCallback(() => {
    closeMenu();
    onLogout();
  }, [closeMenu, onLogout]);

  const handleProfileClick = useCallback(() => {
    closeMenu();
    navigate("/my-profile");
  }, [closeMenu, navigate]);

  return (
    <Box component="span" sx={{ display: "inline-flex" }}>
      <IconButton
        id={buttonId}
        onClick={openMenu}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        aria-label={t("userMenu.openAria")}
        size="small"
        sx={{ p: 0 }}
      >
        <UserAvatar email={email} />
      </IconButton>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { sx: { width: "auto", minWidth: 240, borderRadius: "6px" } },
          list: { "aria-labelledby": buttonId, sx: { py: 1 } },
        }}
      >
        <Box
          component="li"
          sx={{
            listStyle: "none",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1,
          }}
        >
          <UserAvatar email={email} />
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
              {email}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {t("userMenu.signedInAs")}
            </Typography>
          </Box>
        </Box>

        <Divider component="li" sx={{ my: 0.5 }} />

        <MenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("userMenu.profile")} />
        </MenuItem>

        <Box component="li" sx={{ listStyle: "none" }}>
          <ThemeSelector />
        </Box>

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("nav.logout")} />
        </MenuItem>
      </Menu>
    </Box>
  );
}
